"use client"

// Carga por rutas (code splitting): las pantallas van con React.lazy() para que el primer bundle sea
// más liviano; cada pantalla se descarga cuando el usuario navega a esa URL. NavBar, login y rutas
// protegidas siguen importándose al inicio porque el shell de la app los necesita de inmediato.
//
// Todos los import deben ir al principio del archivo (regla ESLint import/first). No pongas
// import después de los lazy() ni de otros const; si no, el linter falla.
//
// Las rutas con componentes lazy deben estar dentro de <Suspense fallback={...}>; React muestra
// LazyFallback (área reservada, sin spinner) mientras descarga el chunk de esa pantalla.

import { lazy, Suspense, useContext, useEffect } from "react"
import "./App.css"
import Box from "@mui/material/Box"

// Componentes síncronos (layout base / auth / protección de rutas)
import PrivateRoute from "./components/adminComponent/PrivateRoute"
import NavBar from "./components/navBar/NavBar.js"
import SessionExpiredModal from "./components/adminComponent/SessionExpiredModal.js"
import PrinterConfigModalHost from "./components/transaccionComponent/PrinterConfigModalHost.js"
import LoginComponent from "./components/adminComponent/LoginComponent.js"
import NoTienesAccesosComponent from "./components/adminComponent/noTienesAccesosComponent.js"
import { SoporteProvider } from "./context/SoporteContext.js"
// Contexto, tema MUI y router: van aquí arriba (mismo motivo import/first), no debajo de lazy().
import { ErpContext } from "./context/ErpContext.js"
import { ThemeProvider } from "@mui/material/styles"
import theme from "./muiThemes/Themes.js"
import { Route, Routes, Navigate, useLocation, useParams } from "react-router-dom"
import { erpCookies } from "./utils/erpCookies.js"

// Pantallas lazy: cada const apunta a un chunk que Webpack/Vite genera por separado.
const HomeDashboard = lazy(() => import("./components/homeComponent/HomeComponent.js"))

// Inventario
const InventaryComponent = lazy(() => import("./components/itemComponent/InventaryComponent.js"))
const EcommerceComponent = lazy(() => import("./components/ecommerceComponent/EcommerceComponent.js"))
const ParametrosEcommerceComponent = lazy(() => import("./components/ecommerceComponent/ParametrosEcommerceComponent.js"))
const CreateItemComponent = lazy(() => import("./components/itemComponent/CreateItemComponent.js"))
const CreateItemCategory = lazy(() => import("./components/itemComponent/CreateItemCategory.js"))
const ItemAtributoComponent = lazy(() => import("./components/itemComponent/ItemAtributoComponent.js"))
const CreateItemProveedor = lazy(() => import("./components/itemComponent/CreateItemProveedor.js"))
const TablaListaCosto = lazy(() => import("./components/itemComponent/TableListaCosto.js"))
const TablaListaPrecio = lazy(() => import("./components/itemComponent/TableListaPrecio.js"))
const RecetaComponent = lazy(() => import("./components/itemComponent/RecetaComponent.js"))
const TablaReceta = lazy(() => import("./components/itemComponent/TablaReceta.js"))

// Configuración (componentes adminComponent; ids ADMINISTRACION*)
const GestionUbicacionComponent = lazy(() => import("./components/adminComponent/GestionUbicacionComponent.js"))
const GestionNegocioComponent = lazy(() => import("./components/adminComponent/GestionNegocioComponent.js"))
const GestionTipoEntidadComponent = lazy(() => import("./components/adminComponent/GestionTipoEntidadComponent.js"))
const GestionEntidadComponent = lazy(() => import("./components/adminComponent/GestionEntidadComponent.js"))
const CrearUsuarioComponent = lazy(() => import("./components/adminComponent/CrearUsuarioComponent.js"))
const TablaEntidadProveedor = lazy(() => import("./components/adminComponent/TablaEntidadProveedor.js"))
const TablaEntidadCliente = lazy(() => import("./components/adminComponent/TablaEntidadCliente.js"))
const CajaComponentAnaliticos = lazy(() => import("./components/adminComponent/CajaComponentAnaliticos.js"))

// Ventas
const GestionGenerarVentaComponent = lazy(() => import("./components/transaccionComponent/GestionGenerarVentaComponent.js"))
const VentaCrearComponent = lazy(() => import("./components/transaccionComponent/venta/VentaCrearComponent.js"))
const GestionDevolucionCanjeComponentVenta = lazy(() => import("./components/transaccionComponent/GestionDevolucionCanjeVenta.js"))
const TablaVentasComponent = lazy(() => import("./components/transaccionComponent/TablaVentasComponent.js"))
const TablaVentasEstados = lazy(() => import("./components/transaccionComponent/TablaVentasEstados.js"))
const PedidoCrearComponent = lazy(() => import("./components/transaccionComponent/pedido/PedidoCrearComponent.js"))
const TablaDetallesVentas = lazy(() => import("./components/transaccionComponent/TablaDetallesVentas.js"))
const PreventaCrearComponent = lazy(() => import("./components/transaccionComponent/preVenta/PreVentaCrearComponent.js"))
const TablaPreVentasComponent = lazy(() => import("./components/transaccionComponent/preVenta/TablaPreVentasComponent.js"))

// Presupuesto
const GestionGenerarPresupuestoComponent = lazy(() => import("./components/transaccionComponent/GestionGenerarPresupuestoComponent.js"))
const TablaPresupuestoComponent = lazy(() => import("./components/transaccionComponent/TablaPresupuestoComponent.js"))
const TablaDetallesPresupuesto = lazy(() => import("./components/transaccionComponent/TablaDetallesPresupuesto.js"))

// Compras
const GestionGenerarCompraComponent = lazy(() => import("./components/transaccionComponent/GestionGenerarCompraComponent.js"))
const TablaComprasComponent = lazy(() => import("./components/transaccionComponent/TablaComprasComponent.js"))
const TablaDetallesCompra = lazy(() => import("./components/transaccionComponent/TablaDetallesCompra.js"))
const EditarCompraComponent = lazy(() => import("./components/transaccionComponent/EditarCompraComponent.js"))

// transaccionComponent: rutas del menú Administración (TRANSACCIONES*) y algunas de Configuración (p. ej. moneda)
const GestionMonedaComponent = lazy(() => import("./components/transaccionComponent/GestionMonedaComponent.js"))
const GestionMedioPagoComponent = lazy(() => import("./components/transaccionComponent/GestionMedioPagoComponent.js"))
const GestionCategoriaTransaccionComponent = lazy(() => import("./components/transaccionComponent/GestionCategoriaTransaccionComponent.js"))
const GestionCategoriaYSubCategoria = lazy(() => import("./components/transaccionComponent/GestionCategoriaYSubCategoria.js"))
const GestionTipoTransaccionComponent = lazy(() => import("./components/transaccionComponent/GestionTipoTransaccionComponent.js"))
const GestionTransaccionComponent = lazy(() => import("./components/transaccionComponent/GestionTransaccionComponent.js"))
const GestionGastosComponent = lazy(() => import("./components/transaccionComponent/GestionGastosComponent.js"))
const TablaTransaccion = lazy(() => import("./components/transaccionComponent/TablaTransaccionComponent.js"))
const TablaTransaccionComponent = lazy(() => import("./components/transaccionComponent/TablaTransaccionComponent.js"))
const TablaCuentaCorriente = lazy(() => import("./components/transaccionComponent/TablaCuentaCorriente.js"))
const GestionImpuestosComponent = lazy(() => import("./components/transaccionComponent/GestionImpuestosComponent.js"))
const TablaVentasAfip = lazy(() => import("./components/transaccionComponent/TablaVentasAfip.js"))
const CajaComponent = lazy(() => import("./components/transaccionComponent/CajaComponent.js"))
const CajaRecalcularComponent = lazy(() => import("./components/transaccionComponent/CajaRecalcularComponent.js"))
const TablaGastosComponent = lazy(() => import("./components/transaccionComponent/TablaGastosComponent.js"))
const RefreshRedirect = lazy(() => import("./components/misc/RefreshRedirect.js"))

// RRHH
const RrhhEmpleadosComponent = lazy(() => import("./components/rrhhComponent/RrhhEmpleadosComponent.js"))
const RrhhTurnosComponent = lazy(() => import("./components/rrhhComponent/RrhhTurnosComponent.js"))
const RrhhFichajesComponent = lazy(() => import("./components/rrhhComponent/RrhhFichajesComponent.js"))
const RrhhAsistenciaComponent = lazy(() => import("./components/rrhhComponent/RrhhAsistenciaComponent.js"))
const RrhhLiquidacionesComponent = lazy(() => import("./components/rrhhComponent/RrhhLiquidacionesComponent.js"))
const RrhhReportesComponent = lazy(() => import("./components/rrhhComponent/RrhhReportesComponent.js"))
const RrhhPlantillasComponent = lazy(() => import("./components/rrhhComponent/RrhhPlantillasComponent.js"))
const RrhhCalendarioComponent = lazy(() => import("./components/rrhhComponent/RrhhCalendarioComponent.js"))
const RrhhTimelineComponent = lazy(() => import("./components/rrhhComponent/RrhhTimelineComponent.js"))
const RrhhConfiguracionComponent = lazy(() => import("./components/rrhhComponent/RrhhConfiguracionComponent.js"))

// Soporte
const CrearTicketComponent = lazy(() => import("./components/soporteComponent/CrearTicketComponent.js"))
const TablaTicketsComponent = lazy(() => import("./components/soporteComponent/TablaTicketsComponent.js"))
const DetalleTicketComponent = lazy(() => import("./components/soporteComponent/DetalleTicketComponent.js"))
const BandejasAgenteComponent = lazy(() => import("./components/soporteComponent/BandejasAgenteComponent.js"))
const MetricasSoporteComponent = lazy(() => import("./components/soporteComponent/MetricasSoporteComponent.js"))
const ConfiguracionSoporteComponent = lazy(() => import("./components/soporteComponent/ConfiguracionSoporteComponent.js"))
const ManualUsuarioComponent = lazy(() => import("./components/soporteComponent/ManualUsuarioComponent.js"))

// Placeholder mientras React descarga el chunk lazy: sin spinner (la pantalla suele tener su propio skeleton).
const LazyFallback = () => <Box sx={{ minHeight: "60vh" }} />

// Función para verificar si el usuario tiene acceso a un menú específico
const tieneAcceso = (menuId, accesos) => {
  return accesos?.some((a) => a.idMenuAcceso === menuId && a.eliminado === false)
}

/** Pantallas bajo nodo grupo: misma regla que NavBar (`groupAccessIds` + `child.id`). */
const MENU_ACCESO_REQUIERE_GRUPO_PADRE = {
  COMPRAS1: "COMPRAS_GRUPO_COMPRAS",
  COMPRAS2: "COMPRAS_GRUPO_COMPRAS",
  COMPRAS3: "COMPRAS_GRUPO_COMPRAS",
}

// Componente para proteger rutas basado en accesos
const ProtectedRoute = ({ children, requiredAccess }) => {
  const { user } = useContext(ErpContext)
  const { tenant } = useParams()

  if (!user) {
    return <Navigate to={`/${tenant || erpCookies.get("tenantURL") || "default"}/login`} replace />
  }

  const userAccesses = user?.accesos || []

  // Si no se especifica acceso requerido, permitir acceso
  if (!requiredAccess) {
    return children
  }

  // Verificar si el usuario tiene el acceso requerido
  if (!tieneAcceso(requiredAccess, userAccesses)) {
    return <NoTienesAccesosComponent />
  }

  const idGrupoPadre = MENU_ACCESO_REQUIERE_GRUPO_PADRE[requiredAccess]
  if (idGrupoPadre && !tieneAcceso(idGrupoPadre, userAccesses)) {
    return <NoTienesAccesosComponent />
  }

  return children
}

const TenantLoginRoute = () => {
  const { tenant } = useParams()
  const { user } = useContext(ErpContext)
  const liveTenant = erpCookies.get("tenant")
  if (user && liveTenant && String(liveTenant) === String(tenant)) {
    return <Navigate to={`/${tenant}/`} replace />
  }
  return <LoginComponent />
}

function App() {
  const { user, isNavbarCollapsed, isSessionExpired } = useContext(ErpContext)
  const location = useLocation()
  const isLoginPage = /\/login\/?$/.test(location.pathname)

  useEffect(() => {
    // console.log(location.pathname)

    const managerDiv = document.querySelector(".ManagerDiv")

    if (managerDiv) {
      managerDiv.style.marginBottom = "0px"
    }
    // console.log("user ", user)
  }, [location.pathname])

  return (
    <ThemeProvider theme={theme}>
      <div className="bodySimulator">
        {/* 🔒 Modal de sesión expirada - solo si hay sesión activa */}
        {user && !isLoginPage && <SessionExpiredModal />}
        {user && !isLoginPage && <PrinterConfigModalHost />}
        
        {user && !isLoginPage && <NavBar />}
        <div 
          className={`containerManagerDiv ${isNavbarCollapsed ? "collapsedNavbar" : ""}`}
          style={{ 
            pointerEvents: isSessionExpired ? 'none' : 'auto',
            opacity: isSessionExpired ? 0.5 : 1,
            transition: 'opacity 0.3s ease'
          }}
        >
          <div className="ManagerDiv">
            <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path=":tenant/login" element={<TenantLoginRoute />} />

              {/* RUTAS PROTEGIDAS */}
              <Route path=":tenant" element={<PrivateRoute />}>
                {/* DASHBOARD */}
                <Route
                  path=""
                  element={
                    <ProtectedRoute requiredAccess="DASHBOARD">
                      <HomeDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE INVENTARIO */}
                <Route
                  path="inventario"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO3">
                      <InventaryComponent />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE ECOMMERCE */}
                <Route
                  path="ecommerce"
                  element={
                    <ProtectedRoute requiredAccess="ECOMMERCE2">
                      <EcommerceComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="ecommerceParametros"
                  element={
                    <ProtectedRoute requiredAccess="ECOMMERCE3">
                      <ParametrosEcommerceComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="crearItem"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO1">
                      <CreateItemComponent />
                    </ProtectedRoute>
                  }
                />
                <Route path="crearItemCategoria" element={<CreateItemCategory />} />
                <Route
                  path="Atributos"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO4">
                      <ItemAtributoComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="itemProveedor"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO1">
                      <CreateItemProveedor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaListaCosto"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS5">
                      <TablaListaCosto />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaListaPrecio"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS8">
                      <TablaListaPrecio />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="recetaComponent"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO2">
                      <RecetaComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaReceta"
                  element={
                    <ProtectedRoute requiredAccess="INVENTARIO5">
                      <TablaReceta />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE VENTAS */}
                <Route
                  path="gestionGenerarVenta/:id?"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS1">
                      <GestionGenerarVentaComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="ventaCrear/:id?"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS10">
                      <VentaCrearComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="preventaCrear"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS11">
                      <PreventaCrearComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaPreventas"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS12">
                      <TablaPreVentasComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaVentas"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS2">
                      <TablaVentasComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="crearPedido"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS9">
                      <PedidoCrearComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaVentasEstados"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS9">
                      <TablaVentasEstados />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionGenerarPresupuesto/:id?"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS3">
                      <GestionGenerarPresupuestoComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaPresupuesto"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS4">
                      <TablaPresupuestoComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="verVentasDetalle"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS5">
                      <TablaDetallesVentas />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="verPresupuestoDetalle"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS6">
                      <TablaDetallesPresupuesto />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionCliente"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS7">
                      <TablaEntidadCliente />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE COMPRAS */}
                <Route
                  path="crearCompra"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS1">
                      <GestionGenerarCompraComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaCompra"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS2">
                      <TablaComprasComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="verCompraDetalle"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS3">
                      <TablaDetallesCompra />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="TablaEntidadProveedor"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS4">
                      <TablaEntidadProveedor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="editarCompraComponent/:idTransaccionEditarCompra/:idEstado"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS2">
                      <EditarCompraComponent />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE ADMINISTRACIÓN (TRANSACCION / TRANSACCIONES*) */}
                <Route
                  path="gestionMedioPago"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES1">
                      <GestionMedioPagoComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionTipoTransaccion"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES2">
                      <GestionTipoTransaccionComponent />
                    </ProtectedRoute>
                  }
                />
                {/* <Route
                  path="gestionTransaccionComponent"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES3">
                      <GestionTransaccionComponent />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path="gestionGastosComponent"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES9">
                      <GestionGastosComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaTransaccionComponent"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES5">
                      <TablaTransaccionComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaCuentaCorriente"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES6">
                      <TablaCuentaCorriente />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaVentasAfip"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES7">
                      <TablaVentasAfip />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="cajaComponentAnaliticos"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES8">
                      <CajaComponentAnaliticos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tablaGastosComponent"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES9">
                      <TablaGastosComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="CajaComponent"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES10">
                      <CajaComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="cajaComponent/recalcular"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES10">
                      <CajaRecalcularComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="CajaComponent/recalcular"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES10">
                      <CajaRecalcularComponent />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE RRHH */}
                <Route
                  path="rrhh/empleados"
                  element={
                    <ProtectedRoute requiredAccess="RRHH1">
                      <RrhhEmpleadosComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/turnos"
                  element={
                    <ProtectedRoute requiredAccess="RRHH2">
                      <RrhhTurnosComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/fichajes"
                  element={
                    <ProtectedRoute requiredAccess="RRHH3">
                      <RrhhFichajesComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/asistencia"
                  element={
                    <ProtectedRoute requiredAccess="RRHH4">
                      <RrhhAsistenciaComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/liquidaciones"
                  element={
                    <ProtectedRoute requiredAccess="RRHH5">
                      <RrhhLiquidacionesComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/reportes"
                  element={
                    <ProtectedRoute requiredAccess="RRHH6">
                      <RrhhReportesComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/plantillas"
                  element={
                    <ProtectedRoute requiredAccess="RRHH7">
                      <RrhhPlantillasComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/calendario"
                  element={
                    <ProtectedRoute requiredAccess="RRHH8">
                      <RrhhCalendarioComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/timeline"
                  element={
                    <ProtectedRoute requiredAccess="RRHH9">
                      <RrhhTimelineComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rrhh/configuracion"
                  element={
                    <ProtectedRoute requiredAccess="RRHH10">
                      <RrhhConfiguracionComponent />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS DE CONFIGURACIÓN (ADMINISTRACION*) */}
                <Route
                  path="gestionMoneda"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION1">
                      <GestionMonedaComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="Impuestos"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION2">
                      <GestionImpuestosComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionCategoria"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION3">
                      <GestionCategoriaYSubCategoria />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionUbicaciones"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION5">
                      <GestionUbicacionComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionEntidad"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION6">
                      <GestionEntidadComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="gestionUsuarios"
                  element={
                    <ProtectedRoute requiredAccess="ADMINISTRACION7">
                      <CrearUsuarioComponent />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS ESPECIALES */}
                <Route
                  path="gestionDevolucionVenta/:idTransaccionDatosDevolucion?"
                  element={
                    <ProtectedRoute requiredAccess="VENTAS2">
                      <GestionDevolucionCanjeComponentVenta />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="transacciones"
                  element={
                    <ProtectedRoute requiredAccess="TRANSACCIONES5">
                      <TablaTransaccion />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="RefreshRedirect/:idTransaccionEditarCompra/:idEstado"
                  element={
                    <ProtectedRoute requiredAccess="COMPRAS2">
                      <RefreshRedirect />
                    </ProtectedRoute>
                  }
                />

                {/* RUTAS SIN PROTECCIÓN ESPECÍFICA */}
                <Route path="gestionNegocio" element={<GestionNegocioComponent />} />
                <Route path="gestionTipoEntidad" element={<GestionTipoEntidadComponent />} />

                {/* RUTAS DE SOPORTE */}
                <Route
                  path="soporte/crear"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE1">
                      <SoporteProvider>
                        <CrearTicketComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="soporte/tickets"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE2">
                      <SoporteProvider>
                        <TablaTicketsComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="soporte/ticket/:id"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE2">
                      <SoporteProvider>
                        <DetalleTicketComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="soporte/bandejas"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE3">
                      <SoporteProvider>
                        <BandejasAgenteComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="soporte/metricas"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE4">
                      <SoporteProvider>
                        <MetricasSoporteComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="soporte/configuracion"
                  element={
                    <ProtectedRoute requiredAccess="SOPORTE5">
                      <SoporteProvider>
                        <ConfiguracionSoporteComponent />
                      </SoporteProvider>
                    </ProtectedRoute>
                  }
                />

                {/* MANUAL DE USUARIO — visible a todo usuario logueado (sin acceso específico) */}
                <Route
                  path="manual"
                  element={
                    <ProtectedRoute>
                      <ManualUsuarioComponent />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
            </Suspense>
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default App
